import React from 'react';
import { 
  Breadcrumbs,
  Box, 
  Button, 
  Container,
  Collapse,
  InputAdornment,
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Link,
  TextField,
  Typography, 
  Chip, 
  IconButton, 
  Menu, 
  MenuItem,
  Skeleton,
  Tooltip,
  useMediaQuery,
  useTheme 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import LogoutNavbar from '../../components/LogoutNavbar';
import GlobalSidebar from '../../components/GlobalSidebar';
import PostLoginFooter from '../../components/PostLoginFooter';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import '../../styles/AdminDashboard.css';
import '../../styles/ManageClubs.css';
import api from '../../api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Import the same icons used in Student.jsx for consistency
import {
  AccountCircle as ProfileIcon,
  FindInPage as LostFoundIcon,
  Lightbulb as SkillsIcon,
  Help as DoubtsIcon,
  Settings as SettingsIcon,
  Groups as ClubsIcon,
  Event as EventsIcon,
} from '@mui/icons-material';

export default function ManageClubs() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [activeClub, setActiveClub] = React.useState(null);
  const [clubs, setClubs] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(true);

  const [filtersOpen, setFiltersOpen] = useState(!isMobile);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const mainContentRef = useRef(null);
  const [menuWidths, setMenuWidths] = useState({ status: null });
  const [openSelectField, setOpenSelectField] = useState(null);

  // Define sidebar items for mobile menu
  const sidebarItems = [
    { 
      label: 'Profile', 
      path: '/admin/profile', 
      action: () => navigate('/admin/profile'),
      icon: <ProfileIcon />
    },
    { 
      label: 'Manage Clubs', 
      path: '/admin/clubs', 
      action: () => navigate('/admin/clubs'),
      icon: <ClubsIcon />
    },
    { 
      label: 'Manage Events', 
      path: '/admin/events', 
      action: () => navigate('/admin/events'),
      icon: <EventsIcon />
    },
    { 
      label: 'Lost and Found', 
      path: '/lostfound', 
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />
    },
    { 
      label: 'Skills', 
      path: '/skills', 
      action: () => navigate('/skills'),
      icon: <SkillsIcon />
    },
    { 
      label: 'Doubts', 
      path: '/yourdoubts', 
      action: () => navigate('/yourdoubts'),
      icon: <DoubtsIcon />
    },
    { 
      label: 'Settings', 
      path: '/settings', 
      action: () => navigate('/settings'),
      icon: <SettingsIcon />
    },
  ];

  const formatDate = (isoString) => {
    if (!isoString) return '';
    
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return isoString;
    }
  };

  useEffect(() => {
    const fetchClubs = async () => {
      setLoadingClubs(true);
      try {
        const response = await api.get('events/club-create-view/');
        const payload = response?.data ?? response;
        const clubsData = Array.isArray(payload)
          ? payload
          : (payload?.results?.data ?? payload?.results ?? payload?.data ?? []);
        
        const safeClubs = Array.isArray(clubsData) ? clubsData : [];
        const formattedClubs = safeClubs.map((club) => {
          const membersCount = Array.isArray(club?.members)
            ? club.members.length
            : (typeof club?.members_count === 'number' ? club.members_count : 0);

          return {
            id: club?.id ?? club?._id ?? club?.name ?? 0,
            name: club?.name || "",
            desc: club?.description || "",
            members: membersCount,
            created: formatDate(club?.created_on || club?.createdAt || club?.created) || "",
            status: (club?.status || "inactive").toString().toLowerCase(),
          };
        });
        
        setClubs(formattedClubs);
      } catch (error) {
        // console.log("Error occurred in fetching clubs", error);
        setClubs([]);
      } finally {
        setLoadingClubs(false);
      }
    };
    
    fetchClubs();
  }, []);

  useEffect(() => {
    setFiltersOpen(!isMobile);
  }, [isMobile]);

  const filteredClubs = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const status = (statusFilter || 'all').toLowerCase();

    return (Array.isArray(clubs) ? clubs : []).filter((club) => {
      const matchesStatus = status === 'all' ? true : club?.status === status;
      if (!matchesStatus) return false;

      if (!query) return true;
      const haystack = `${club?.name ?? ''} ${club?.desc ?? ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [clubs, searchTerm, statusFilter]);

  const hasActiveFilters = searchTerm.trim().length > 0 || statusFilter !== 'all';

  const handleMenuOpen = (event, club) => {
    setAnchorEl(event.currentTarget);
    setActiveClub(club ?? null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveClub(null);
  };

  const handleUpdate = () => {
    const club = activeClub;
    if (club) navigate(`/admin/clubs/${club.id}/edit`);
    handleMenuClose();
  };

  const handleManageMembersFiltered = () => {
    const club = activeClub;
    if (club) {
      navigate(
        `/admin/memberships?club=${encodeURIComponent(club.name)}&from=clubs`,
        { state: { from: 'clubs' } }
      );
    }
    handleMenuClose();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const handleSelectOpen = (field) => (event) => {
    const width = event.currentTarget?.getBoundingClientRect().width;
    if (width) {
      setMenuWidths((prev) => ({ ...prev, [field]: width }));
    }
    setOpenSelectField(field);
  };

  const handleSelectClose = () => {
    setOpenSelectField(null);
  };

  const getMenuProps = (field) => {
    const width = menuWidths[field];
    return {
      disablePortal: false,
      disableScrollLock: true,
      container: mainContentRef.current,
      sx: {
        zIndex: 1300,
      },
      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
      transformOrigin: { vertical: 'top', horizontal: 'left' },
      marginThreshold: null,
      transitionDuration: 120,
      PaperProps: {
        sx: {
          mt: 0,
          width: width || undefined,
          minWidth: width || undefined,
          maxWidth: width || undefined,
        },
      },
    };
  };

  const exportToPDF = () => {
    const records = filteredClubs;
    const today = new Date();
    const dateStamp = today.toISOString().split('T')[0];
    const timeStamp = today.toLocaleString();

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('Clubs Export', 40, 42);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Generated: ${timeStamp}`, 40, 62);
    doc.text(`Records: ${records.length}`, 40, 78);

    const activeFilterLabels = [];
    if (searchTerm.trim()) activeFilterLabels.push(`Search: ${searchTerm.trim()}`);
    if ((statusFilter || 'all') !== 'all') activeFilterLabels.push(`Status: ${statusFilter}`);

    if (activeFilterLabels.length > 0) {
      const filtersLine = `Filters: ${activeFilterLabels.join('  •  ')}`;
      const lines = doc.splitTextToSize(filtersLine, 760);
      doc.text(lines, 40, 94);
    }

    const columns = ['Club Name', 'Description', 'Members', 'Created On', 'Status'];
    const rows = records.map((c) => [
      c?.name || '—',
      c?.desc || '—',
      typeof c?.members === 'number' ? String(c.members) : (c?.members ?? '—'),
      c?.created || '—',
      (c?.status || 'inactive').toString().toUpperCase(),
    ]);

    const tableMarginX = 28;
    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: activeFilterLabels.length > 0 ? 120 : 105,
      theme: 'grid',
      margin: { left: tableMarginX, right: tableMarginX },
      tableWidth: 'auto',
      styles: {
        font: 'helvetica',
        fontSize: 8.3,
        cellPadding: 4,
        overflow: 'linebreak',
        valign: 'top',
      },
      headStyles: {
        fillColor: [13, 28, 79],
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 350 },
        2: { cellWidth: 70, halign: 'center' },
        3: { cellWidth: 95 },
        4: { cellWidth: 85 },
      },
      didDrawPage: () => {
        const pageCount = doc.getNumberOfPages();
        const pageNumber = doc.getCurrentPageInfo().pageNumber;
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(
          `Page ${pageNumber} of ${pageCount}`,
          doc.internal.pageSize.getWidth() - tableMarginX,
          doc.internal.pageSize.getHeight() - 18,
          { align: 'right' }
        );
      },
    });

    doc.save(`clubs_export_${dateStamp}.pdf`);
  };

  const handleBackNavigation = () => {
    navigate('/admin/dashboard');
  };

  const renderDesktopTable = () => (
    <TableContainer className="mc-table-container">
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Club Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="center">Members</TableCell>
            <TableCell>Created On</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loadingClubs ? (
            Array.from({ length: 6 }).map((_, idx) => (
              <TableRow key={`mc-sk-${idx}`}>
                <TableCell><Skeleton width={180} /></TableCell>
                <TableCell><Skeleton width={320} /></TableCell>
                <TableCell align="center"><Skeleton width={40} /></TableCell>
                <TableCell><Skeleton width={120} /></TableCell>
                <TableCell><Skeleton width={70} /></TableCell>
                <TableCell><Skeleton width={36} /></TableCell>
              </TableRow>
            ))
          ) : filteredClubs.length > 0 ? (
            filteredClubs.map((c, index) => (
              <TableRow key={c.id ?? index}>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <span className="mc-name">{c.name}</span>
                  </Box>
                </TableCell>
                <TableCell className="mc-desc-cell">
                  {c.desc ? (
                    <span className="mc-desc-text" title={c.desc}>
                      {c.desc}
                    </span>
                  ) : (
                    <span className="mc-muted">—</span>
                  )}
                </TableCell>
                <TableCell align="center">{c.members}</TableCell>
                <TableCell>{c.created || <span className="mc-muted">—</span>}</TableCell>
                <TableCell>
                  <Chip
                    label={c.status}
                    color={c.status === 'active' ? 'success' : 'error'}
                    size="small"
                    sx={{ fontWeight: 800, textTransform: 'uppercase' }}
                  />
                </TableCell>
                <TableCell align="center" className="mc-actions-cell">
                  <Tooltip title="Actions">
                    <IconButton
                      aria-label="actions"
                      size="small"
                      onClick={(e) => handleMenuOpen(e, c)}
                      className="mc-actions-btn"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          ) : clubs.length > 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="mc-empty">
                No clubs match the current filters
                {hasActiveFilters && (
                  <Box sx={{ marginTop: 1 }}>
                    <Button variant="text" size="small" onClick={handleClearFilters} className="mc-clear-btn">
                      Clear filters
                    </Button>
                  </Box>
                )}
              </TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell colSpan={6} className="mc-empty">
                No clubs found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMobileCards = () => (
    <Box className="mc-card-list">
      {loadingClubs ? (
        Array.from({ length: 4 }).map((_, idx) => (
          <Paper key={`mc-m-sk-${idx}`} className="mc-club-card" elevation={0}>
            <Box className="mc-club-card-header">
              <Skeleton width={160} height={22} />
              <Skeleton width={36} height={36} />
            </Box>
            <Skeleton width="100%" height={18} style={{ marginTop: 10 }} />
            <Skeleton width="70%" height={18} />
            <Box className="mc-club-card-meta">
              <Skeleton width="100%" height={44} />
              <Skeleton width="100%" height={44} />
            </Box>
          </Paper>
        ))
      ) : filteredClubs.length > 0 ? (
        filteredClubs.map((c, index) => (
          <Paper key={c.id || index} className="mc-club-card" elevation={0}>
            <Box className="mc-club-card-header">
              <Box className="mc-club-card-primary">
                <Typography variant="subtitle1" className="mc-club-card-name">
                  {c.name}
                </Typography>
                <Chip
                  label={c.status}
                  color={c.status === 'active' ? 'success' : 'error'}
                  size="small"
                  sx={{ fontWeight: 800, textTransform: 'uppercase' }}
                />
              </Box>
              <IconButton
                aria-label="actions"
                size="small"
                onClick={(e) => handleMenuOpen(e, c)}
                className="mc-actions-btn"
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>

            {c.desc ? (
              <Typography variant="body2" className="mc-club-card-desc" title={c.desc}>
                {c.desc}
              </Typography>
            ) : (
              <Typography variant="body2" className="mc-club-card-desc mc-muted">
                No description
              </Typography>
            )}

            <Box className="mc-club-card-meta">
              <Box className="mc-club-card-field">
                <Typography variant="caption" className="mc-club-card-label">Members</Typography>
                <Typography variant="body2" className="mc-club-card-value">{c.members}</Typography>
              </Box>
              <Box className="mc-club-card-field">
                <Typography variant="caption" className="mc-club-card-label">Created On</Typography>
                <Typography variant="body2" className="mc-club-card-value">{c.created || '—'}</Typography>
              </Box>
            </Box>
          </Paper>
        ))
      ) : (
        <Paper className="mc-empty" elevation={0}>
          <Typography variant="body2" align="center">
            {clubs.length > 0 ? 'No clubs match the current filters' : 'No clubs found'}
          </Typography>
          {clubs.length > 0 && hasActiveFilters && (
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 1 }}>
              <Button variant="text" size="small" onClick={handleClearFilters} className="mc-clear-btn">
                Clear filters
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );

  return (
    <div className="admin-dashboard manage-clubs">
      {/* Pass sidebarItems to Navbar for mobile */}
      <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />
      <div className="dashboard-layout">
        {/* Only show sidebar on desktop */}
        {!isMobile && <GlobalSidebar />}
        <main className="dashboard-main">
          <Container ref={mainContentRef} maxWidth="xl" className="dashboard-container">
            <header className="mc-page-header">
              <Box className="mc-header-left">
                <Box className="mc-breadcrumb-row">
                  <Tooltip title="Back">
                    <IconButton onClick={handleBackNavigation} className="mc-back-icon" size="medium" aria-label="Back">
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Breadcrumbs
                    className="mc-breadcrumbs"
                    separator={<NavigateNextIcon fontSize="small" />}
                    aria-label="breadcrumb"
                  >
                    <Link
                      underline="hover"
                      color="inherit"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/admin/dashboard');
                      }}
                      href="/admin/dashboard"
                      className="mc-breadcrumb-link"
                    >
                      Dashboard
                    </Link>
                    <Typography color="text.primary" className="mc-breadcrumb-current">
                      Clubs
                    </Typography>
                  </Breadcrumbs>
                </Box>

                <Box className="mc-title-wrap">
                  <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" className="mc-page-title">
                    Manage Clubs
                  </Typography>
                  <Typography variant="body2" className="mc-page-subtitle" color="text.secondary">
                    Create clubs, review details, and manage membership.
                  </Typography>
                </Box>
              </Box>

              <Box className="mc-header-actions">
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={exportToPDF}
                  className="mc-export-btn"
                  disabled={loadingClubs || clubs.length === 0}
                >
                  Export PDF ({filteredClubs.length})
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/admin/clubs/new')}
                  className="mc-add-btn"
                >
                  Add New Club
                </Button>
              </Box>
            </header>

            <Paper className="mc-card" elevation={0}>
              <Box className="mc-card-header">
                <Box>
                  <Typography variant="h6" className="mc-card-title">
                    All Clubs
                  </Typography>
                  <Typography className="mc-card-subtitle">
                    {loadingClubs
                      ? 'Loading…'
                      : (filteredClubs.length === clubs.length
                        ? `${clubs.length} club${clubs.length === 1 ? '' : 's'}`
                        : `Showing ${filteredClubs.length} of ${clubs.length} clubs`)}
                  </Typography>
                </Box>

                <Box className="mc-card-header-actions">
                  {isMobile && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setFiltersOpen((v) => !v)}
                      className="mc-filters-toggle"
                    >
                      {filtersOpen ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                  )}
                </Box>
              </Box>

              <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
                <Box className="mc-filters-bar">
                  <Box className="mc-filters-row">
                    <TextField
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      size="small"
                      placeholder="Search clubs…"
                      className="mc-search"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      select
                      label="Status"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      size="small"
                      className="mc-status"
                      sx={{ minWidth: 160 }}
                      SelectProps={{
                        MenuProps: getMenuProps('status'),
                        onOpen: handleSelectOpen('status'),
                        onClose: handleSelectClose,
                        open: openSelectField === 'status',
                      }}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                    </TextField>

                    <Box sx={{ flex: 1 }} />

                    <Button
                      variant="text"
                      size="small"
                      onClick={handleClearFilters}
                      disabled={!hasActiveFilters}
                      className="mc-clear-btn"
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              </Collapse>

              {!isMobile ? renderDesktopTable() : renderMobileCards()}

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleUpdate}>Modify</MenuItem>
                <MenuItem onClick={handleManageMembersFiltered}>Manage Members</MenuItem>
              </Menu>
            </Paper>
          </Container>
        </main>
      </div>
      <PostLoginFooter />
    </div>
  );
}